import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";

type Variante = "primario" | "secundario" | "perigo" | "confirmar" | "fantasma";

const estilos: Record<Variante, string> = {
  primario: "bg-tinta text-white hover:bg-black",
  secundario: "bg-chapa text-tinta border-2 border-tinta hover:bg-concreto",
  perigo: "bg-erro text-white hover:brightness-95",
  confirmar: "bg-ok text-white hover:brightness-95",
  fantasma: "bg-transparent text-tinta underline-offset-4 hover:underline",
};

export function Botao({
  variante = "primario", grande = false, className = "", ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante; grande?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors
        disabled:cursor-not-allowed disabled:opacity-50 ${grande ? "min-h-16 px-6 text-xl" : "min-h-11 px-4 text-base"}
        ${estilos[variante]} ${className}`}
    />
  );
}

export function Secao({ titulo, acoes, children, className = "" }: {
  titulo?: ReactNode; acoes?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <section className={`rounded-lg border-2 border-aco bg-chapa ${className}`}>
      {(titulo || acoes) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-aco px-4 py-3">
          {titulo && <h2 className="font-sinal text-xl font-semibold">{titulo}</h2>}
          {acoes && <div className="flex flex-wrap gap-2">{acoes}</div>}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export type Tom = "neutro" | "ok" | "erro" | "alerta" | "info" | "faixa";
const tons: Record<Tom, string> = {
  neutro: "bg-concreto text-tinta",
  ok: "bg-ok-claro text-ok",
  erro: "bg-erro-claro text-erro",
  alerta: "bg-alerta-claro text-alerta",
  info: "bg-info-claro text-info",
  faixa: "bg-faixa text-tinta",
};

export function Selo({ tom = "neutro", children }: { tom?: Tom; children: ReactNode }) {
  return <span className={`inline-flex items-center rounded px-2 py-0.5 text-sm font-semibold ${tons[tom]}`}>{children}</span>;
}

export function Campo({ rotulo, ajuda, className = "", ...props }: InputHTMLAttributes<HTMLInputElement> & { rotulo: string; ajuda?: string }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-sm font-semibold text-aco-escuro">{rotulo}</span>
      <input
        {...props}
        className="min-h-11 rounded-md border-2 border-aco bg-chapa px-3 text-base focus:border-tinta focus:outline-none"
      />
      {ajuda && <span className="text-sm text-aco-escuro">{ajuda}</span>}
    </label>
  );
}

export function Escolha({ rotulo, children, className = "", ...props }: SelectHTMLAttributes<HTMLSelectElement> & { rotulo: string }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-sm font-semibold text-aco-escuro">{rotulo}</span>
      <select
        {...props}
        className="min-h-11 rounded-md border-2 border-aco bg-chapa px-3 text-base focus:border-tinta focus:outline-none"
      >
        {children}
      </select>
    </label>
  );
}

export function Carregando({ texto = "Carregando" }: { texto?: string }) {
  return <p className="p-6 text-center text-aco-escuro" role="status">{texto}...</p>;
}

export function Vazio({ children }: { children: ReactNode }) {
  return <p className="rounded-md border-2 border-dashed border-aco p-6 text-center text-aco-escuro">{children}</p>;
}

export function MensagemErro({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <p className="rounded-md bg-erro-claro px-3 py-2 font-medium text-erro" role="alert">{children}</p>;
}

/** Tabela simples com cabeçalho fixo e rolagem horizontal no celular. */
export function Tabela({ cabecalho, children }: { cabecalho: ReactNode[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b-2 border-tinta">
            {cabecalho.map((c, i) => <th key={i} className="whitespace-nowrap px-2 py-2 text-sm font-semibold">{c}</th>)}
          </tr>
        </thead>
        <tbody className="[&>tr]:border-b [&>tr]:border-aco [&_td]:px-2 [&_td]:py-2">{children}</tbody>
      </table>
    </div>
  );
}
